import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { z } from 'zod';
import { useThemeVar } from '@norkron/web-themer/react';
import { withConformance } from '@norkron/web-conformance';
import { createScopedErrors, registerCodes } from '@norkron/web-errors';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useRTL } from '@norkron/web-a11y';
import { useSyncExternalStore } from 'react';

// src/index.ts
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var LIB_ID = "@norkron/web-motion";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function selfCheck(root, opts) {
  const start = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const instances = Array.from(
      root.querySelectorAll('[data-norkron-lib^="@norkron/web-motion"]')
    );
    if (instances.length === 0) return findings;
    for (const instance of instances) {
      const now = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
      if (now - start > safeOpts.budgetMs) {
        findings.push({
          libId: LIB_ID,
          code: "MT_SELFCHECK_BUDGET_EXCEEDED",
          category: "perf",
          severity: "info",
          message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms; partial results returned.`,
          extras: { budgetMs: safeOpts.budgetMs, elapsed: now - start }
        });
        break;
      }
      const prefersReduced = typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (prefersReduced) {
        const animated = instance.matches('[data-motion-active="true"]') ? instance : instance.querySelector('[data-motion-active="true"]');
        if (animated) {
          findings.push({
            libId: LIB_ID,
            code: "MT_REDUCED_MOTION_NOT_RESPECTED",
            category: "a11y",
            severity: "major",
            message: "Animation runs despite the user's prefers-reduced-motion: reduce setting.",
            selector: selectorFor(animated),
            suggestedFix: "Gate the animation via useReducedMotion() and skip / shorten it."
          });
        }
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "MT_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}

// src/motion-math.ts
function sampleCubicBezier(x1, y1, x2, y2, n = 64) {
  if (typeof n !== "number" || Number.isNaN(n)) return [];
  if (n < 0) {
    throw new RangeError(
      `sampleCubicBezier: n must be >= 0 (got ${n})`
    );
  }
  if (n === 0) return [];
  const pts = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const mt = 1 - t;
    const x = 3 * mt * mt * t * x1 + 3 * mt * t * t * x2 + t * t * t;
    const y = 3 * mt * mt * t * y1 + 3 * mt * t * t * y2 + t * t * t;
    pts.push([x, y]);
  }
  return pts;
}
function springCurve(stiffness, damping, mass, durationMs, n = 200) {
  const omega0 = Math.sqrt(stiffness / mass);
  const zeta = damping / (2 * Math.sqrt(stiffness * mass));
  const pts = [];
  const CRITICAL_EPS = 1e-9;
  for (let i = 0; i <= n; i++) {
    const t = i / n * (durationMs / 1e3);
    let x;
    if (Math.abs(zeta - 1) < CRITICAL_EPS) {
      x = (1 + omega0 * t) * Math.exp(-omega0 * t);
    } else if (zeta > 1) {
      const omegaD = omega0 * Math.sqrt(zeta * zeta - 1);
      x = Math.exp(-zeta * omega0 * t) * (Math.cosh(omegaD * t) + zeta * omega0 / omegaD * Math.sinh(omegaD * t));
    } else {
      const omegaD = omega0 * Math.sqrt(1 - zeta * zeta);
      x = Math.exp(-zeta * omega0 * t) * (Math.cos(omegaD * t) + zeta * omega0 / omegaD * Math.sin(omegaD * t));
    }
    pts.push([i / n, 1 - x]);
  }
  return pts;
}
var CSS_NUMBER = String.raw`[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?`;
var CUBIC_BEZIER_RE = new RegExp(
  `cubic-bezier\\(\\s*(${CSS_NUMBER})\\s*,\\s*(${CSS_NUMBER})\\s*,\\s*(${CSS_NUMBER})\\s*,\\s*(${CSS_NUMBER})\\s*\\)`
);
function parseBezier(s) {
  if (typeof s !== "string") {
    throw new TypeError(
      `parseBezier: expected string, received ${s === null ? "null" : typeof s}`
    );
  }
  const m = s.match(CUBIC_BEZIER_RE);
  if (!m) return [0, 0, 1, 1];
  const x1 = parseFloat(m[1]);
  const y1 = parseFloat(m[2]);
  const x2 = parseFloat(m[3]);
  const y2 = parseFloat(m[4]);
  if (x1 < 0 || x1 > 1) {
    throw new SyntaxError(
      `parseBezier: x1 must be in [0, 1] per CSS Easing Functions spec (got ${x1})`
    );
  }
  if (x2 < 0 || x2 > 1) {
    throw new SyntaxError(
      `parseBezier: x2 must be in [0, 1] per CSS Easing Functions spec (got ${x2})`
    );
  }
  return [x1, y1, x2, y2];
}
function dampingRatio(stiffness, damping, mass) {
  if (!Number.isNaN(stiffness) && stiffness <= 0) {
    throw new RangeError(
      `dampingRatio: stiffness must be > 0 (got ${stiffness})`
    );
  }
  if (!Number.isNaN(mass) && mass <= 0) {
    throw new RangeError(
      `dampingRatio: mass must be > 0 (got ${mass})`
    );
  }
  return damping / (2 * Math.sqrt(stiffness * mass));
}
function springSettle(stiffness, damping, mass, epsilon = 1e-3, maxSeconds = 5, n = 2e3) {
  const omega0 = Math.sqrt(stiffness / mass);
  const zeta = damping / (2 * Math.sqrt(stiffness * mass));
  const CRITICAL_EPS = 1e-9;
  for (let i = n; i >= 0; i--) {
    const t = i / n * maxSeconds;
    let displacement;
    if (Math.abs(zeta - 1) < CRITICAL_EPS) {
      displacement = (1 + omega0 * t) * Math.exp(-omega0 * t);
    } else if (zeta > 1) {
      const omegaD = omega0 * Math.sqrt(zeta * zeta - 1);
      displacement = Math.exp(-zeta * omega0 * t) * (Math.cosh(omegaD * t) + zeta * omega0 / omegaD * Math.sinh(omegaD * t));
    } else {
      const omegaD = omega0 * Math.sqrt(1 - zeta * zeta);
      displacement = Math.exp(-zeta * omega0 * t) * (Math.cos(omegaD * t) + zeta * omega0 / omegaD * Math.sin(omegaD * t));
    }
    const value = 1 - displacement;
    if (Math.abs(value - 1) > epsilon) {
      const settleTime = Math.min((i + 1) / n * maxSeconds, maxSeconds);
      return settleTime;
    }
  }
  return 0;
}
function cubicBezierAtT(x1, y1, x2, y2, t) {
  const mt = 1 - t;
  const mt2 = mt * mt;
  const t2 = t * t;
  const x = 3 * mt2 * t * x1 + 3 * mt * t2 * x2 + t2 * t;
  const y = 3 * mt2 * t * y1 + 3 * mt * t2 * y2 + t2 * t;
  return [x, y];
}
function cubicBezierSolve(x1, y1, x2, y2, progress, epsilon = 1e-6) {
  if (!Number.isFinite(x1) || !Number.isFinite(y1) || !Number.isFinite(x2) || !Number.isFinite(y2)) {
    throw new TypeError(
      `cubicBezierSolve: control points must be finite (got x1=${x1}, y1=${y1}, x2=${x2}, y2=${y2})`
    );
  }
  if (Number.isNaN(progress)) return NaN;
  if (progress <= 0) return 0;
  if (progress >= 1) return 1;
  if (x1 === y1 && x2 === y2) return progress;
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t2) => ((ax * t2 + bx) * t2 + cx) * t2;
  const sampleDX = (t2) => (3 * ax * t2 + 2 * bx) * t2 + cx;
  const sampleY = (t2) => ((ay * t2 + by) * t2 + cy) * t2;
  let t = progress;
  for (let i = 0; i < 8; i++) {
    const xErr = sampleX(t) - progress;
    if (Math.abs(xErr) < epsilon) return sampleY(t);
    const dx = sampleDX(t);
    if (Math.abs(dx) < 1e-12) break;
    t -= xErr / dx;
  }
  let lo = 0;
  let hi = 1;
  t = progress;
  for (let i = 0; i < 64; i++) {
    const xVal = sampleX(t);
    if (Math.abs(xVal - progress) < epsilon) return sampleY(t);
    if (xVal < progress) {
      lo = t;
    } else {
      hi = t;
    }
    t = (lo + hi) / 2;
  }
  return sampleY(t);
}
var EasingCurveSVGSchema = z.object({ easing: z.string(), color: z.string(), size: z.number().positive().optional(), gridColor: z.string().optional() }).passthrough();
var MOTION_LIB = "@norkron/web-motion";
var MOTION_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.motion");
var { warn, panic, reportInvalidInput } = createScopedErrors(MOTION_LIB, MOTION_TOKEN);
try {
  registerCodes(MOTION_LIB, {
    MOTION_INVALID_PRESET: {
      description: "`getSpringPreset(name)` was called with a name that is not in the `springPresets` registry. The function then throws RangeError as before; the warn surfaces the same condition through structured telemetry.",
      severity: "warning",
      docsAnchor: "motion/invalid-preset",
      exampleFix: "Use one of the documented preset names (bouncy, gentle, stiff, wobbly, slow, snappy, molasses, responsive). The thrown error message lists every valid name."
    },
    MOTION_QUERY_FAILED: {
      description: "The `prefers-reduced-motion` matchMedia query could not be evaluated (no `window` or `matchMedia` available). The hook then falls back to `false` (motion enabled), matching the legacy behavior.",
      severity: "warning",
      docsAnchor: "motion/query-failed",
      exampleFix: "In server/SSR contexts this is expected \u2014 the false fallback hydrates to the client value. In a browser, ensure the runtime is not blocking matchMedia (e.g. a stripped JSDOM)."
    },
    MT_REDUCED_MOTION_NOT_RESPECTED: {
      description: `selfCheck observed an animation running on an instance with [data-motion-active="true"] despite the user's prefers-reduced-motion: reduce setting.`,
      severity: "warning",
      docsAnchor: "web-motion/self-check#reduced-motion-not-respected",
      exampleFix: "Gate the animation via useReducedMotion() and skip or shorten it for users who prefer reduced motion."
    },
    MT_SELFCHECK_BUDGET_EXCEEDED: {
      description: "selfCheck exceeded its per-call time budget while walking motion instances; partial results returned.",
      severity: "info",
      docsAnchor: "web-motion/self-check#budget-exceeded",
      exampleFix: "Increase budgetMs or reduce the number of motion instances scanned per page."
    },
    MT_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck threw internally while inspecting a motion instance. Exception caught + reported as a finding.",
      severity: "info",
      docsAnchor: "web-motion/self-check#internal-error",
      exampleFix: "Inspect finding.message + finding.extras for the underlying error."
    }
  });
} catch {
}

// src/version.ts
var LIB_VERSION = "0.7.4";

// src/zod-bridge.ts
function toZodLikeSafeParseError(failure) {
  return failure;
}
function EasingCurveSVG(props) {
  const parsed = EasingCurveSVGSchema.safeParse(props);
  if (!parsed.success) {
    reportInvalidInput({ fn: "EasingCurveSVG", parseResult: toZodLikeSafeParseError(parsed) });
  }
  const { easing, color, size = 160, gridColor } = props;
  const themedGrid = useThemeVar("--motion-curve-stroke", "var(--n-700)");
  const resolvedGrid = gridColor ?? themedGrid;
  const [x1, y1, x2, y2] = parseBezier(easing);
  const pts = sampleCubicBezier(x1, y1, x2, y2);
  const pad = 16;
  const w = size;
  const h = size;
  const gw = w - pad * 2;
  const gh = h - pad * 2;
  const pathD = pts.map(([x, y], i) => {
    const px = pad + x * gw;
    const py = pad + (1 - y) * gh;
    return `${i === 0 ? "M" : "L"} ${px.toFixed(2)} ${py.toFixed(2)}`;
  }).join(" ");
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: w,
      height: h,
      viewBox: `0 0 ${w} ${h}`,
      style: { display: "block" },
      ...withConformance("@norkron/web-motion", LIB_VERSION),
      children: [
        /* @__PURE__ */ jsx("line", { x1: pad, y1: pad, x2: pad, y2: pad + gh, stroke: resolvedGrid, strokeWidth: 1 }),
        /* @__PURE__ */ jsx(
          "line",
          {
            x1: pad,
            y1: pad + gh,
            x2: pad + gw,
            y2: pad + gh,
            stroke: resolvedGrid,
            strokeWidth: 1
          }
        ),
        /* @__PURE__ */ jsx(
          "line",
          {
            x1: pad,
            y1: pad + gh,
            x2: pad + gw,
            y2: pad,
            stroke: resolvedGrid,
            strokeWidth: 1,
            strokeDasharray: "4 4"
          }
        ),
        /* @__PURE__ */ jsx("path", { d: pathD, fill: "none", stroke: color, strokeWidth: 2.5, strokeLinecap: "round" }),
        /* @__PURE__ */ jsx("circle", { cx: pad + x1 * gw, cy: pad + (1 - y1) * gh, r: 3, fill: color, opacity: 0.6 }),
        /* @__PURE__ */ jsx("circle", { cx: pad + x2 * gw, cy: pad + (1 - y2) * gh, r: 3, fill: color, opacity: 0.6 })
      ]
    }
  );
}
var SpringOverlaySVGSchema = z.object({ presets: z.array(z.object({ name: z.string(), stiffness: z.number(), damping: z.number(), mass: z.number() })), colors: z.array(z.string()).optional(), maxMs: z.number().positive().optional(), width: z.number().positive().optional(), height: z.number().positive().optional(), gridColor: z.string().optional(), labelColor: z.string().optional(), fontFamily: z.string().optional() }).passthrough();
var DEFAULT_COLORS = ["#b0b0b0", "#8a8a8a", "#6a6a6a", "#4a4a4a"];
function SpringOverlaySVG(props) {
  const parsed = SpringOverlaySVGSchema.safeParse(props);
  if (!parsed.success) {
    reportInvalidInput({ fn: "SpringOverlaySVG", parseResult: toZodLikeSafeParseError(parsed) });
  }
  const {
    presets,
    colors = DEFAULT_COLORS,
    maxMs = 700,
    width: w = 600,
    height: h = 200,
    gridColor,
    labelColor,
    fontFamily = "monospace"
  } = props;
  const themedGrid = useThemeVar("--motion-overlay-fill", "var(--n-700)");
  const themedLabel = useThemeVar("--motion-overlay-label", "var(--n-400)");
  const resolvedGrid = gridColor ?? themedGrid;
  const resolvedLabel = labelColor ?? themedLabel;
  const rtl = useRTL();
  const pad = 40;
  const gw = w - pad * 2;
  const gh = h - pad * 2;
  const yLabelX = rtl ? pad + gw + 6 : pad - 6;
  const yLabelAnchor = rtl ? "start" : "end";
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      width: "100%",
      viewBox: `0 0 ${w} ${h}`,
      style: { display: "block" },
      ...withConformance("@norkron/web-motion", LIB_VERSION),
      children: [
        /* @__PURE__ */ jsx("line", { x1: pad, y1: pad, x2: pad, y2: pad + gh, stroke: resolvedGrid, strokeWidth: 1 }),
        /* @__PURE__ */ jsx(
          "line",
          {
            x1: pad,
            y1: pad + gh,
            x2: pad + gw,
            y2: pad + gh,
            stroke: resolvedGrid,
            strokeWidth: 1
          }
        ),
        [120, 240, 400, 600].map((ms) => {
          const x = pad + ms / maxMs * gw;
          return /* @__PURE__ */ jsxs("g", { children: [
            /* @__PURE__ */ jsx(
              "line",
              {
                x1: x,
                y1: pad,
                x2: x,
                y2: pad + gh,
                stroke: resolvedGrid,
                strokeWidth: 1,
                strokeDasharray: "3 3"
              }
            ),
            /* @__PURE__ */ jsxs(
              "text",
              {
                x,
                y: pad + gh + 16,
                fill: resolvedLabel,
                fontSize: 10,
                textAnchor: "middle",
                fontFamily,
                children: [
                  ms,
                  "ms"
                ]
              }
            )
          ] }, ms);
        }),
        /* @__PURE__ */ jsx(
          "text",
          {
            x: yLabelX,
            y: pad + 4,
            fill: resolvedLabel,
            fontSize: 10,
            textAnchor: yLabelAnchor,
            fontFamily,
            children: "1.0"
          }
        ),
        /* @__PURE__ */ jsx(
          "text",
          {
            x: yLabelX,
            y: pad + gh + 4,
            fill: resolvedLabel,
            fontSize: 10,
            textAnchor: yLabelAnchor,
            fontFamily,
            children: "0.0"
          }
        ),
        presets.map((preset, pi) => {
          const pts = springCurve(preset.stiffness, preset.damping, preset.mass, maxMs);
          const d = pts.map(([t, val], i) => {
            const x = pad + t * gw;
            const y = pad + (1 - val) * gh;
            return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
          }).join(" ");
          return /* @__PURE__ */ jsx(
            "path",
            {
              d,
              fill: "none",
              stroke: colors[pi % colors.length],
              strokeWidth: 2,
              strokeLinecap: "round",
              opacity: 0.9
            },
            pi
          );
        })
      ]
    }
  );
}
var getSpringPresetSchema = z.object({ name: z.enum(["bouncy", "gentle", "stiff", "wobbly", "slow", "snappy", "molasses", "responsive"]) });

// src/spring-presets.ts
var springPresets = {
  bouncy: {
    name: "bouncy",
    stiffness: 300,
    damping: 10,
    mass: 1
  },
  gentle: {
    name: "gentle",
    stiffness: 120,
    damping: 14,
    mass: 1
  },
  stiff: {
    name: "stiff",
    stiffness: 400,
    damping: 35,
    mass: 1
  },
  wobbly: {
    name: "wobbly",
    stiffness: 180,
    damping: 12,
    mass: 1
  },
  slow: {
    name: "slow",
    stiffness: 60,
    damping: 14,
    mass: 1
  },
  snappy: {
    name: "snappy",
    stiffness: 500,
    damping: 30,
    mass: 1
  },
  molasses: {
    name: "molasses",
    stiffness: 40,
    damping: 12,
    mass: 2
  },
  responsive: {
    name: "responsive",
    stiffness: 250,
    damping: 26,
    mass: 1
  }
};
function getSpringPreset(name) {
  const parsed = getSpringPresetSchema.safeParse({ name });
  if (!parsed.success) {
    reportInvalidInput({ fn: "getSpringPreset", parseResult: toZodLikeSafeParseError(parsed) });
  }
  const preset = springPresets[name];
  if (preset === void 0) {
    const valid = Object.keys(springPresets).join(", ");
    try {
      warn({
        fn: "getSpringPreset",
        code: "MOTION_INVALID_PRESET",
        message: `getSpringPreset: unknown preset '${name}'.`,
        prop: "name",
        received: name,
        expected: `one of: ${valid}`,
        dedupKey: `getSpringPreset:${String(name)}`
      });
    } catch {
    }
    throw new RangeError(
      `getSpringPreset: unknown preset '${name}'. Valid presets: ${valid}`
    );
  }
  return preset;
}
var REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    if (typeof window !== "undefined") {
      try {
        warn({
          fn: "prefersReducedMotion",
          code: "MOTION_QUERY_FAILED",
          message: "window.matchMedia is unavailable; falling back to false (motion enabled).",
          prop: "window.matchMedia",
          received: typeof window.matchMedia,
          expected: "function",
          dedupKey: "prefersReducedMotion:no-matchMedia"
        });
      } catch {
      }
    }
    return false;
  }
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}
function useReducedMotion() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
function subscribe(onStoreChange) {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => {
    };
  }
  const mql = window.matchMedia(REDUCED_MOTION_QUERY);
  if (typeof mql.addEventListener === "function") {
    mql.addEventListener("change", onStoreChange);
    return () => mql.removeEventListener("change", onStoreChange);
  }
  mql.addListener(onStoreChange);
  return () => mql.removeListener(onStoreChange);
}
function getSnapshot() {
  return prefersReducedMotion();
}
function getServerSnapshot() {
  return false;
}
function withReducedMotion(durationMs) {
  return prefersReducedMotion() ? 0 : durationMs;
}

// src/index.ts
registerLazy(
  "@norkron/web-motion",
  () => selfCheck
);

export { EasingCurveSVG, LIB_VERSION, SpringOverlaySVG, cubicBezierAtT, cubicBezierSolve, dampingRatio, getSpringPreset, parseBezier, prefersReducedMotion, sampleCubicBezier, selfCheck, springCurve, springPresets, springSettle, useReducedMotion, withReducedMotion };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map